import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import {useLocation, useNavigationType} from '@github-ui/react-router'
import {isOnInboxPath} from '@github-ui/shared-dashboard-surface/helpers/is-on-inbox'
import {deriveSidebarLink} from '@github-ui/shared-dashboard-surface/helpers/layout-helpers'
import type {SidebarLink} from '@github-ui/saved-views'
import {ensureDefaultSort, queryMap} from '../helpers/query-resolver'

import type {PullsDashboardLayoutPayload, PullsInboxMaxAge as PullsInboxMaxAgeType} from '../types/routes-types'
import {PullsInboxMaxAge} from '../types/routes-types'
import {useListResults} from '../hooks/use-list-results'
import {PULLS_PATH} from '../constants/routes'
import {
  ScreenReaderAnnouncementStateContext,
  useScreenReaderAnnouncementState,
} from '@github-ui/shared-dashboard-surface/hooks/use-screen-reader-announcements'

type FetchResults = (args: {q: string; page: number; sidebarLinkId?: string}) => void
type SetSectionPreferences = (prefs: {order: string[]; hidden: string[]}) => void
interface PullsDashboardActionsContextValue {
  fetchResults: FetchResults
  setMaxPrAge: (maxPrAge: PullsInboxMaxAgeType) => void
  setSectionPreferences: SetSectionPreferences
}

const PullsDashboardPayloadContext = createContext<PullsDashboardLayoutPayload | null>(null)
const PullsDashboardRawPayloadContext = createContext<PullsDashboardLayoutPayload | null>(null)
const PullsDashboardFetchedQueryContext = createContext<string | null>(null)
const PullsDashboardCurrentPageContext = createContext<number | null>(null)
const PullsDashboardLocationSidebarLinkIdContext = createContext<string | undefined | null>(null)
const PullsDashboardIsLoadingContext = createContext<boolean | null>(null)
const PullsDashboardMaxPrAgeContext = createContext<PullsInboxMaxAgeType | null>(null)
const PullsDashboardActionsContext = createContext<PullsDashboardActionsContextValue | null>(null)
const PullsDashboardSavedViewsContext = createContext<SidebarLink[] | null>(null)
const PullsDashboardSelectedSidebarLinkContext = createContext<SidebarLink | undefined | null>(null)
const SetPullsDashboardSavedViewsContext = createContext<Dispatch<SetStateAction<SidebarLink[]>> | null>(null)
const SetPullsDashboardSelectedSidebarLinkContext = createContext<Dispatch<
  SetStateAction<SidebarLink | undefined>
> | null>(null)

type Listener = () => void

// Context provides one stable store per dashboard. Consumers subscribe to one section field
// so recording another section's loading state does not invalidate every rendered section.
interface PullsDashboardLoadingStateStore {
  getSectionRowCount: (sectionId: string) => number | undefined
  getSectionCollapsedState: (sectionId: string) => boolean | undefined
  subscribeToSectionRowCount: (sectionId: string, listener: Listener) => () => void
  subscribeToSectionCollapsedState: (sectionId: string, listener: Listener) => () => void
  recordSectionRowCount: (sectionId: string, count: number) => void
  recordSectionCollapsedState: (sectionId: string, isCollapsed: boolean) => void
}

function addListener(listenersBySection: Map<string, Set<Listener>>, sectionId: string, listener: Listener) {
  let listeners = listenersBySection.get(sectionId)
  if (!listeners) {
    listeners = new Set()
    listenersBySection.set(sectionId, listeners)
  }
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) listenersBySection.delete(sectionId)
  }
}

function notifyListeners(listenersBySection: Map<string, Set<Listener>>, sectionId: string) {
  for (const listener of listenersBySection.get(sectionId) ?? []) listener()
}

function createPullsDashboardLoadingStateStore(): PullsDashboardLoadingStateStore {
  const sectionRowCounts = new Map<string, number>()
  const sectionCollapsedStates = new Map<string, boolean>()
  const rowCountListeners = new Map<string, Set<Listener>>()
  const collapsedStateListeners = new Map<string, Set<Listener>>()

  return {
    getSectionRowCount: sectionId => sectionRowCounts.get(sectionId),
    getSectionCollapsedState: sectionId => sectionCollapsedStates.get(sectionId),
    subscribeToSectionRowCount: (sectionId, listener) => addListener(rowCountListeners, sectionId, listener),
    subscribeToSectionCollapsedState: (sectionId, listener) =>
      addListener(collapsedStateListeners, sectionId, listener),
    recordSectionRowCount: (sectionId, count) => {
      if (sectionRowCounts.get(sectionId) === count) return
      sectionRowCounts.set(sectionId, count)
      notifyListeners(rowCountListeners, sectionId)
    },
    recordSectionCollapsedState: (sectionId, isCollapsed) => {
      if (sectionCollapsedStates.get(sectionId) === isCollapsed) return
      sectionCollapsedStates.set(sectionId, isCollapsed)
      notifyListeners(collapsedStateListeners, sectionId)
    },
  }
}

const NOOP_LOADING_STATE_STORE: PullsDashboardLoadingStateStore = {
  getSectionRowCount: () => undefined,
  getSectionCollapsedState: () => undefined,
  subscribeToSectionRowCount: () => () => {},
  subscribeToSectionCollapsedState: () => () => {},
  recordSectionRowCount: () => {},
  recordSectionCollapsedState: () => {},
}
const PullsDashboardLoadingStateStoreContext = createContext(NOOP_LOADING_STATE_STORE)

export function PullsDashboardSurfaceStateProvider({
  children,
  payload,
  isLoading,
}: {
  children: ReactNode
  payload: PullsDashboardLayoutPayload
  isLoading: boolean
}) {
  const location = useLocation()
  const navigationType = useNavigationType()
  const {pathname} = location
  const isOnInbox = isOnInboxPath({pathname})

  const {fetchedQuery, navigationQuery, currentPage, fetchResults, locationSidebarLinkId} = useListResults({
    defaultFilterValue: isOnInbox ? '' : queryMap().authored,
  })
  const screenReaderAnnouncementState = useScreenReaderAnnouncementState()
  const route = location.pathname + location.search
  const observedRouteRef = useRef(route)
  useEffect(() => {
    if (observedRouteRef.current === route) return
    observedRouteRef.current = route
    if (navigationType === 'REPLACE') return screenReaderAnnouncementState.suppressTarget(navigationQuery, currentPage)
    screenReaderAnnouncementState.ensureNavigationPending(navigationQuery, currentPage)
  }, [currentPage, navigationQuery, navigationType, route, screenReaderAnnouncementState])
  const fetchResultsWithAnnouncement = useCallback<FetchResults>(
    args => {
      screenReaderAnnouncementState.markNavigationPending(ensureDefaultSort(args.q), args.page)
      fetchResults(args)
    },
    [fetchResults, screenReaderAnnouncementState],
  )
  const [savedViews, setSavedViews] = useState<SidebarLink[]>(() =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- assertion required for type narrowing; removing it breaks tsc
    payload.savedViews.map(savedView => ({...savedView, searchType: 'pull-requests'}) as SidebarLink),
  )
  const [selectedSidebarLink, setSelectedSidebarLink] = useState<SidebarLink | undefined>(() =>
    deriveSidebarLink({
      sidebarLinkId: locationSidebarLinkId,
      basePath: PULLS_PATH,
      savedViews,
      sidebarLinks: payload.sidebarLinks ?? [],
      location,
    }),
  )

  const [maxPrAge, setMaxPrAge] = useState<PullsInboxMaxAgeType>(payload.filters?.maxPrAge ?? PullsInboxMaxAge.OneMonth)

  const [pendingPrefs, setPendingPrefs] = useState<{order: string[]; hidden: string[]} | null>(null)
  const [loadingStateStore] = useState(createPullsDashboardLoadingStateStore)

  // Compute the effective optimistic prefs inline: once the payload reflects
  // what we saved, ignore `pendingPrefs` so future renders read straight from
  // the payload.
  const sectionPrefs = pendingPrefs && !payloadMatchesPrefs(payload, pendingPrefs) ? pendingPrefs : null

  const setSectionPreferences = useCallback(({order, hidden}: {order: string[]; hidden: string[]}) => {
    setPendingPrefs({order, hidden})
  }, [])

  const effectivePayload = useMemo<PullsDashboardLayoutPayload>(() => {
    if (!sectionPrefs) {
      return payload
    }
    // Hidden sections are removed from inboxSectionQueries server-side; mirror that
    // for the optimistic update so the render filter doesn't leave hidden sections
    // in the rendered list until the next layout refetch. Conversely, when a user
    // un-hides a section that the server had previously omitted, add it back so it
    // becomes renderable immediately (consumers fall back to the section id when
    // no query string is provided).
    let inboxSectionQueries = payload.inboxSectionQueries
    if (inboxSectionQueries) {
      const hiddenSet = new Set(sectionPrefs.hidden)
      const knownIds = new Set<string>([
        ...Object.keys(inboxSectionQueries),
        ...(payload.hiddenSections ?? []),
        ...(payload.sectionOrder ?? []),
        ...sectionPrefs.order,
      ])
      const next: Record<string, string> = {}
      for (const id of knownIds) {
        if (hiddenSet.has(id)) continue
        next[id] = inboxSectionQueries[id] ?? id
      }
      inboxSectionQueries = next
    }
    return {
      ...payload,
      inboxSectionQueries,
      sectionOrder: sectionPrefs.order,
      hiddenSections: sectionPrefs.hidden,
    }
  }, [payload, sectionPrefs])

  const actions = useMemo<PullsDashboardActionsContextValue>(
    () => ({fetchResults: fetchResultsWithAnnouncement, setMaxPrAge, setSectionPreferences}),
    [fetchResultsWithAnnouncement, setSectionPreferences],
  )

  return (
    <ScreenReaderAnnouncementStateContext value={screenReaderAnnouncementState}>
      <PullsDashboardPayloadContext value={effectivePayload}>
        <PullsDashboardRawPayloadContext value={payload}>
          <PullsDashboardFetchedQueryContext value={fetchedQuery}>
            <PullsDashboardCurrentPageContext value={currentPage}>
              <PullsDashboardLocationSidebarLinkIdContext value={locationSidebarLinkId}>
                <PullsDashboardIsLoadingContext value={isLoading}>
                  <PullsDashboardMaxPrAgeContext value={maxPrAge}>
                    <PullsDashboardActionsContext value={actions}>
                      <SetPullsDashboardSavedViewsContext value={setSavedViews}>
                        <SetPullsDashboardSelectedSidebarLinkContext value={setSelectedSidebarLink}>
                          <PullsDashboardSavedViewsContext value={savedViews}>
                            <PullsDashboardSelectedSidebarLinkContext value={selectedSidebarLink}>
                              <PullsDashboardLoadingStateStoreContext value={loadingStateStore}>
                                {children}
                              </PullsDashboardLoadingStateStoreContext>
                            </PullsDashboardSelectedSidebarLinkContext>
                          </PullsDashboardSavedViewsContext>
                        </SetPullsDashboardSelectedSidebarLinkContext>
                      </SetPullsDashboardSavedViewsContext>
                    </PullsDashboardActionsContext>
                  </PullsDashboardMaxPrAgeContext>
                </PullsDashboardIsLoadingContext>
              </PullsDashboardLocationSidebarLinkIdContext>
            </PullsDashboardCurrentPageContext>
          </PullsDashboardFetchedQueryContext>
        </PullsDashboardRawPayloadContext>
      </PullsDashboardPayloadContext>
    </ScreenReaderAnnouncementStateContext>
  )
}

export function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index])
}

function payloadMatchesPrefs(
  payload: PullsDashboardLayoutPayload,
  prefs: {order: string[]; hidden: string[]},
): boolean {
  return arraysEqual(payload.sectionOrder ?? [], prefs.order) && arraysEqual(payload.hiddenSections ?? [], prefs.hidden)
}

export function usePullsDashboardPayload() {
  const context = use(PullsDashboardPayloadContext)
  if (!context) {
    throw new Error('usePullsDashboardPayload must be used within a PullsDashboardSurfaceStateProvider')
  }
  return context
}

export function usePullsDashboardSavedViews() {
  const context = use(PullsDashboardSavedViewsContext)
  if (!context) {
    throw new Error('usePullsDashboardSavedViews must be used within a PullsDashboardSurfaceStateProvider')
  }
  return context
}

export function usePullsDashboardSelectedSidebarLink() {
  const context = use(PullsDashboardSelectedSidebarLinkContext)
  if (context === null) {
    throw new Error('usePullsDashboardSelectedSidebarLink must be used within a PullsDashboardSurfaceStateProvider')
  }
  return context
}

export function useSetPullsDashboardSavedViews() {
  const context = use(SetPullsDashboardSavedViewsContext)
  if (!context) {
    throw new Error('useSetPullsDashboardSavedViews must be used within a PullsDashboardSurfaceStateProvider')
  }
  return context
}

export function useSetPullsDashboardSelectedSidebarLink() {
  const context = use(SetPullsDashboardSelectedSidebarLinkContext)
  if (!context) {
    throw new Error('useSetPullsDashboardSelectedSidebarLink must be used within a PullsDashboardSurfaceStateProvider')
  }
  return context
}

export function useOptionalPullsDashboardPayload() {
  return use(PullsDashboardPayloadContext)
}

export function useOptionalPullsDashboardRawPayload() {
  return use(PullsDashboardRawPayloadContext)
}

export function usePullsDashboardFetchedQuery() {
  const value = use(PullsDashboardFetchedQueryContext)
  if (value === null) {
    throw new Error('usePullsDashboardFetchedQuery must be used within a PullsDashboardSurfaceStateProvider')
  }
  return value
}

export function usePullsDashboardCurrentPage() {
  const value = use(PullsDashboardCurrentPageContext)
  if (value === null) {
    throw new Error('usePullsDashboardCurrentPage must be used within a PullsDashboardSurfaceStateProvider')
  }
  return value
}

export function usePullsDashboardLocationSidebarLinkId() {
  const value = use(PullsDashboardLocationSidebarLinkIdContext)
  if (value === null) {
    throw new Error('usePullsDashboardLocationSidebarLinkId must be used within a PullsDashboardSurfaceStateProvider')
  }
  return value
}

export function usePullsDashboardIsLoading() {
  const value = use(PullsDashboardIsLoadingContext)
  if (value === null) {
    throw new Error('usePullsDashboardIsLoading must be used within a PullsDashboardSurfaceStateProvider')
  }
  return value
}

export function usePullsDashboardMaxPrAge() {
  const value = use(PullsDashboardMaxPrAgeContext)
  if (value === null) {
    throw new Error('usePullsDashboardMaxPrAge must be used within a PullsDashboardSurfaceStateProvider')
  }
  return value
}

export function usePullsDashboardActions() {
  const value = use(PullsDashboardActionsContext)
  if (!value) {
    throw new Error('usePullsDashboardActions must be used within a PullsDashboardSurfaceStateProvider')
  }
  return value
}

export function usePullsDashboardSectionRowCount(sectionId: string) {
  const store = use(PullsDashboardLoadingStateStoreContext)
  const subscribe = useCallback(
    (listener: Listener) => store.subscribeToSectionRowCount(sectionId, listener),
    [sectionId, store],
  )
  const getSnapshot = useCallback(() => store.getSectionRowCount(sectionId), [sectionId, store])
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function usePullsDashboardSectionCollapsedState(sectionId: string) {
  const store = use(PullsDashboardLoadingStateStoreContext)
  const subscribe = useCallback(
    (listener: Listener) => store.subscribeToSectionCollapsedState(sectionId, listener),
    [sectionId, store],
  )
  const getSnapshot = useCallback(() => store.getSectionCollapsedState(sectionId), [sectionId, store])
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function usePullsDashboardLoadingStateActions() {
  const store = use(PullsDashboardLoadingStateStoreContext)
  return {
    recordSectionRowCount: store.recordSectionRowCount,
    recordSectionCollapsedState: store.recordSectionCollapsedState,
  }
}
